import { useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useCreateDriverExpenseMutation, useGetDriverExpenses, useDeleteDriverExpenseMutation, useUpdateDriverExpenseMutation } from "../../../../../api/fleet-management/driver-expenses/driver-expenses.hooks";
import DeleteModal from "../../../../components/DeleteModal";
import { formatDriverExpenseAmount, normalizeDriverExpenses } from "../../DriverExpenses/utils/driver-expense.utils";

import { MdDelete, MdEdit } from "react-icons/md";

const initialForm = {
  driverId: "",
  noOfDays: "",
  salaryPerDay: "",
  totalAmount: "",
};

const getDriverIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const getExpenseIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const BookingDriverExpensesSection = ({ driverId, driverName, vehicleLabel }) => {
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [editingExpenseId, setEditingExpenseId] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const normalizedDriverId = getDriverIdValue(driverId);
  const { data, isLoading, isError, error } = useGetDriverExpenses(
    normalizedDriverId ? { driverId: normalizedDriverId } : null
  );
  const { mutate: createDriverExpense, isLoading: isCreating } = useCreateDriverExpenseMutation();
  const { mutate: updateDriverExpense, isLoading: isUpdating } = useUpdateDriverExpenseMutation();
  const { mutate: deleteDriverExpense, isLoading: isDeleting } = useDeleteDriverExpenseMutation();

  const isSaving = isCreating || isUpdating;

  const expenses = useMemo(() => normalizeDriverExpenses(data), [data]);
  const grandTotalAmount = useMemo(() => {
    return expenses.reduce((sum, expense) => {
      const amount = Number(expense?.totalAmount);
      return sum + (Number.isFinite(amount) ? amount : 0);
    }, 0);
  }, [expenses]);

  const openModal = () => {
    setFeedback({ type: "", message: "" });
    setEditingExpenseId("");
    setForm((prev) => ({
      ...initialForm,
      driverId: normalizedDriverId,
      noOfDays: prev.noOfDays,
      salaryPerDay: prev.salaryPerDay,
      totalAmount: prev.totalAmount,
    }));
    setShowModal(true);
  };

  const closeModal = () => {
    if (isSaving) return;
    setShowModal(false);
    setEditingExpenseId("");
  };

  const handleEditExpense = (expense) => {
    const expenseId = getExpenseIdValue(expense);
    if (!expenseId) return;

    setFeedback({ type: "", message: "" });
    setEditingExpenseId(expenseId);
    setForm({
      driverId: normalizedDriverId,
      noOfDays: String(expense?.noOfDays ?? ""),
      salaryPerDay: String(expense?.salaryPerDay ?? ""),
      totalAmount: String(expense?.totalAmount ?? ""),
    });
    setShowModal(true);
  };

  const handleDeleteExpense = (expense) => {
    const expenseId = getExpenseIdValue(expense);
    if (!expenseId || isDeleting) return;

    setSelectedExpense(expense);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
    setSelectedExpense(null);
  };

  const handleConfirmDeleteExpense = () => {
    const expenseId = getExpenseIdValue(selectedExpense);
    if (!expenseId) return;

    setFeedback({ type: "", message: "" });

    deleteDriverExpense(expenseId, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Driver expense deleted successfully." });
        closeDeleteModal();
      },
      onError: (deleteError) => {
        setFeedback({
          type: "danger",
          message: deleteError?.response?.data?.message || "Failed to delete driver expense.",
        });
      },
    });
  };

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "noOfDays" || key === "salaryPerDay") {
        const days = Number(key === "noOfDays" ? value : prev.noOfDays);
        const salary = Number(key === "salaryPerDay" ? value : prev.salaryPerDay);
        if (Number.isFinite(days) && Number.isFinite(salary) && days > 0 && salary > 0) {
          next.totalAmount = String(days * salary);
        }
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    const payload = {
      driverId: normalizedDriverId,
      noOfDays: Number(form.noOfDays),
      salaryPerDay: Number(form.salaryPerDay),
      totalAmount: Number(form.totalAmount),
    };

    if (
      !payload.driverId ||
      !Number.isFinite(payload.noOfDays) ||
      payload.noOfDays <= 0 ||
      !Number.isFinite(payload.salaryPerDay) ||
      payload.salaryPerDay <= 0 ||
      !Number.isFinite(payload.totalAmount) ||
      payload.totalAmount <= 0
    ) {
      setFeedback({
        type: "warning",
        message: "Please fill in all fields with valid values.",
      });
      return;
    }

    if (editingExpenseId) {
      updateDriverExpense(
        {
          driverExpenseId: editingExpenseId,
          driverExpenseData: payload,
        },
        {
          onSuccess: () => {
            setFeedback({ type: "success", message: "Driver expense updated successfully." });
            setShowModal(false);
            setEditingExpenseId("");
            setForm(initialForm);
          },
          onError: (updateError) => {
            setFeedback({
              type: "danger",
              message: updateError?.response?.data?.message || "Failed to update driver expense.",
            });
          },
        }
      );
      return;
    }

    createDriverExpense(payload, {
      onSuccess: () => {
        setFeedback({ type: "success", message: "Driver expense created successfully." });
        setShowModal(false);
        setForm(initialForm);
      },
      onError: (createError) => {
        setFeedback({
          type: "danger",
          message: createError?.response?.data?.message || "Failed to create driver expense.",
        });
      },
    });
  };

  if (!normalizedDriverId) return null;

  return (
    <div className="card">
      <div className="card-header border-0 pb-0 d-flex align-items-center justify-content-between">
        <div>
          <h5 className="mb-0">Driver Expenses</h5>
          <small className="text-muted">
            {driverName || "Assigned driver"}
            {vehicleLabel ? ` • ${vehicleLabel}` : ""}
          </small>
        </div>
        <Button variant="primary" size="sm" onClick={openModal} disabled={isDeleting}>
          Add Expense
        </Button>
      </div>

      <div className="card-body pt-3">
        {feedback.message && (
          <Alert variant={feedback.type} className="mb-3">
            {feedback.message}
          </Alert>
        )}

        {isLoading ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : isError ? (
          <Alert variant="danger" className="mb-0">
            Failed to load driver expenses. {error?.response?.data?.message || "Please try again."}
          </Alert>
        ) : expenses.length === 0 ? (
          <div className="text-muted">No expenses found for this driver.</div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>No. of Days</th>
                  <th>Salary/Day</th>
                  <th>Total Amount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {expenses.map((expense) => (
                  <tr key={expense?._id || expense?.id}>
                    <td>{expense?.noOfDays ?? "-"}</td>
                    <td>{formatDriverExpenseAmount(expense?.salaryPerDay)}</td>
                    <td>{formatDriverExpenseAmount(expense?.totalAmount)}</td>
                    <td>
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleEditExpense(expense)}
                          disabled={isDeleting || isSaving}
                        >
                          <MdEdit size={18} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => handleDeleteExpense(expense)}
                          disabled={isDeleting || isSaving}
                        >
                          <MdDelete size={18} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2} className="fw-semibold">
                    Grand Total
                  </td>
                  <td className="fw-semibold">{formatDriverExpenseAmount(grandTotalAmount)}</td>
                  <td />
                </tr>
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showModal} onHide={closeModal} centered>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title>{editingExpenseId ? "Edit Driver Expense" : "Add Driver Expense"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <p className="text-muted mb-3">
              {driverName || "Selected driver"}
            </p>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>No. of Days</Form.Label>
                  <Form.Control
                    type="number"
                    min="0.5"
                    step="0.5"
                    value={form.noOfDays}
                    onChange={(e) => updateField("noOfDays", e.target.value)}
                    disabled={isSaving}
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Wages</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.salaryPerDay}
                    onChange={(e) => updateField("salaryPerDay", e.target.value)}
                    disabled={isSaving}
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Total Amount</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={form.totalAmount}
                    onChange={(e) => updateField("totalAmount", e.target.value)}
                    disabled={isSaving}
                    placeholder="0"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeModal} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isSaving}>
              {isSaving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  {editingExpenseId ? "Updating..." : "Saving..."}
                </>
              ) : (
                editingExpenseId ? "Update Expense" : "Save Expense"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <DeleteModal
        show={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={handleConfirmDeleteExpense}
        title="Delete Driver Expense"
        message="Are you sure you want to delete this driver expense? This action cannot be undone."
        confirmText="Delete Expense"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default BookingDriverExpensesSection;
